import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl, FormattedMessage, FormattedDate } from 'react-intl';
import { FaFilePdf, FaEye, FaEyeSlash, FaClipboardList } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import { injectStateService, StateService } from '@/shared/services/StateService';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import FormattedListValue from '@/shared/components/FormattedListValue';
import { LinkIcon, IconPencil, CommentIcon } from '@/shared/components/Icons';
import DocumentService from '@/shared/services/DocumentService';
import { CanRead, CanUpdate, CanDelete } from '@/shared/components/security';
import RelationName from '@/shared/components/RelationName';
import ResourceName from '@/shared/components/ResourceName';
import DataCacheService from '@/shared/services/DataCacheService';
import authService, { features, functionalities, privileges } from '@/shared/services/AuthService';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import messages from '../invoicingMessages';
import RegistrationRemarkTooltip from './RegistrationRemarkTooltip';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import { mapRegTaskName } from '../../../shared/services/ObjectDisplayService';
import Checkbox from '@/shared/components/input/Checkbox';
import AuthService from '@/shared/services/AuthService';

class ToInvoiceDetail extends PureComponent {
  state = {
    users: DataCacheService.getAllUsers().reduce((o, u) => {
      if (!u.id) return o;
      // eslint-disable-next-line no-param-reassign
      o[u.id] = u.name;
      return o;
    }, {}),
  };
  /**
   *
   */
  constructor(props) {
    super(props);
    this.showReferenceYear = AuthService.hasFeature(features.ReferenceYear);
    this.allowRegistrationLocking = !!DataCacheService.getDefaultCompany()?.allowRegistrationLocking;
  }

  render() {
    const {
      invoiceStreamId,
      columns,
      registrations,
      nextSchedule,
      advances,
      stateService,
      intl,
      onEditRegistration,
      onAddToDraftInvoice,
      onNeverInvoiceRegistration,
      onRemoveRegistration,
      onToggleInForfait,
      onToggleBillable,
      onToggleLocked,
    } = this.props;
    const { users } = this.state;
    const showRelation =
      registrations && registrations.some(r => r.invoiceStreamId !== invoiceStreamId);

    const showInForfait = registrations && registrations.some(r => r.inForfait !== null);

    return (
      <Fragment>
        {registrations && registrations.length > 0 && (
          <tr className="subclasses">
            <td />
            <td colSpan={columns - 1}>
              <div className="subtable-wrapper width100pct">
                <table className="table subtable">
                  <caption>
                    <FormattedMessage {...messages.Registrations} />
                  </caption>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage {...messages.Date} />
                      </th>
                      {this.showReferenceYear ? (
                        <th className="year-column">
                          <FormattedMessage {...messages.ReferenceYear} />
                        </th>
                      ) : null}
                      {showRelation && (
                        <th>
                          <FormattedMessage {...messages.Relation} />
                        </th>
                      )}
                      <th>
                        <FormattedMessage {...messages.Employee} />
                      </th>
                      <th />
                      <th>
                        <FormattedMessage {...messages.Resource} />
                      </th>
                      <th className="text-right">
                        <FormattedMessage {...messages.Quantity} />
                      </th>
                      <th className="text-right">
                        <FormattedMessage {...messages.UnitPrice} />
                      </th>
                      <th className="text-right">
                        <FormattedMessage {...messages.Amount} />
                      </th>
                      <th className="text-center">
                        <FormattedMessage {...messages.Billable} />
                      </th>
                      {showInForfait && (
                        <th className="text-center">
                          <FormattedMessage {...messages.InForfait} />
                        </th>
                      )}
                      <th />
                      <th className="text-right">
                        <FormattedMessage {...messages.Actions} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map(r => (
                      <tr key={r.id}>
                        <td>
                          <FormattedDateShort value={r.date} />
                        </td>
                        {this.showReferenceYear ? (
                          <td className="year-column">{r.referenceYear || ''}</td>
                        ) : null}
                        {showRelation && (
                          <td>
                            <RelationName relation={r.relation} assignment={r.invoiceStream} />
                          </td>
                        )}
                        <td>{users[r.userId]}</td>
                        <td className="text-right">
                          {!!r.plannedTaskId && (
                            <PopoverStickOnHover
                              trigger={['hover', 'focus']}
                              placement="left"
                              component={mapRegTaskName(r, intl)}
                              shouldUpdatePosition
                            >
                              <span className="margin10L explanation-icon pull-right">
                                <FaClipboardList />
                              </span>
                            </PopoverStickOnHover>
                          )}
                        </td>
                        <td>
                          <ResourceName resource={r.resource} />
                        </td>
                        <td className="text-right">
                          {r.type === 0 ? r.durationMinutes.minuteToTime() : r.quantity}
                        </td>
                        <td className="text-right">
                          <FormattedCurrency value={r.amount} />
                        </td>
                        <td className="text-right">
                          <FormattedCurrency value={r.totalAmount} />
                        </td>
                        <td className="text-center">
                          <Checkbox
                            onClick={checked => onToggleBillable(invoiceStreamId, r, checked)}
                            value={r.isBillable}
                            disabled={
                              !authService.hasPermission({
                                functionality: functionalities.InvoiceDraft,
                                privilege: privileges.Update,
                              })
                            }
                          />
                        </td>
                        {showInForfait && (
                          <td className="text-center">
                            {r.inForfait !== null && (
                              <Checkbox
                                onClick={checked => onToggleInForfait(invoiceStreamId, r, checked)}
                                value={r.inForfait}
                                disabled={
                                  !authService.hasPermission({
                                    functionality: functionalities.InvoiceDraft,
                                    privilege: privileges.Update,
                                  })
                                }
                              />
                            )}
                          </td>
                        )}
                        <td>
                          {!!(r.remark || r.internalRemark) && (
                            <PopoverStickOnHover
                              trigger={['hover', 'focus']}
                              placement="left"
                              component={
                                <RegistrationRemarkTooltip
                                  remark={r.remark}
                                  internalRemark={r.internalRemark}
                                />
                              }
                              shouldUpdatePosition
                            >
                              <span className="margin10L explanation-icon pull-right">
                                <CommentIcon />
                              </span>
                            </PopoverStickOnHover>
                          )}
                        </td>
                        <td className="text-right">
                          <div className="flex-row-nowrap">
                            <CanUpdate on="InvoiceDraft">
                              <a
                                title={intl.formatMessage(messages.AddToInvoiceProposal)}
                                className="btn btn-icon btn-sm"
                                onClick={e => {
                                  onAddToDraftInvoice(r);
                                  e.stopPropagation();
                                }}
                              >
                                {!r.addingToDraft && (
                                  <React.Fragment>
                                    <i className="fa fa-money" />
                                    <i className="fa fa-arrow-right" />
                                  </React.Fragment>
                                )}
                                {r.addingToDraft && <i className="fa fa-spinner fa-spin" />}
                              </a>
                            </CanUpdate>
                            {this.allowRegistrationLocking && (
                            <CanUpdate on="InvoiceDraft">
                              <a
                                title={intl.formatMessage(r.isLocked ? messages.UnlockRegistration : messages.LockRegistration)}
                                className={classNames('btn btn-icon btn-sm', { active: r.isLocked })}
                                onClick={e => {
                                  onToggleLocked(invoiceStreamId, r, !r.isLocked);
                                  e.stopPropagation();
                                }}
                              >
                                <i class="fa fa-lock fa-fw"></i>
                              </a>
                            </CanUpdate>
                            )}
                            <CanUpdate on="InvoiceDraft">
                              <a
                                title={intl.formatMessage(messages.Edit)}
                                className="btn btn-icon btn-sm"
                                disabled={r.isLocked }
                                onClick={e => {                                  
                                  onEditRegistration(r);
                                  e.stopPropagation();
                                }}
                              >
                                <IconPencil />
                              </a>
                            </CanUpdate>
                            <CanUpdate on="InvoiceDraft">
                              <a
                                title={intl.formatMessage(messages.NeverInvoiceRegistration)}
                                className="btn btn-icon btn-sm"
                                disabled={r.isLocked }
                                onClick={e => {                                  
                                  onNeverInvoiceRegistration(r);
                                  e.stopPropagation();
                                }}
                              >
                                {!r.neverInvoicing && (
                                  <React.Fragment>
                                    <i
                                      style={{ verticalAlign: 'middle' }}
                                      className="fa fa-pencil-square-o"
                                    />
                                    <i
                                      style={{ verticalAlign: 'baseline', marginLeft: '2px' }}
                                      className="fa fa-times"
                                    />
                                  </React.Fragment>
                                )}
                                {r.neverInvoicing && <i className="fa fa-spinner fa-spin" />}
                              </a>
                            </CanUpdate>
                            <CanDelete on="RegistrationOwn">
                              <a
                                title={intl.formatMessage(messages.RemoveRegistration)}
                                className="btn btn-icon btn-sm"
                                disabled={
                                  r.isLocked ||
                                  (r.userId !== authService.getUserId() &&
                                  !authService.hasPermission({
                                    functionality: functionalities.RegistrationOthers,
                                    privilege: privileges.Delete,
                                  }))
                                }
                                onClick={e => {
                                  if (
                                    r.userId !== authService.getUserId() &&
                                    !authService.hasPermission({
                                      functionality: functionalities.RegistrationOthers,
                                      privilege: privileges.Delete,
                                    })
                                  )
                                    return;
                                  onRemoveRegistration(r);
                                  e.stopPropagation();
                                }}
                              >
                                {!r.removing && <i className="fa fa-trash-o" />}
                                {r.removing && <i className="fa fa-spinner fa-spin" />}
                              </a>
                            </CanDelete>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        )}
        {nextSchedule && (
          <tr>
            <td className="middle">
              <h4 className="rotated-text">
                <FormattedMessage {...messages.FixedAmount} />
              </h4>
            </td>
            <td colSpan={columns - 1}>
              <table className="table subtable">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...messages.NextScheduleDate} />
                    </th>
                    <th>
                      <FormattedMessage {...messages.RegistrationsUntil} />
                    </th>
                    <th>
                      <FormattedMessage {...messages.Amount} />
                    </th>
                    <th>
                      <FormattedMessage {...messages.InvoiceText} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {!!nextSchedule.invoiceCreationDate && (
                        <FormattedDateShort value={nextSchedule.invoiceCreationDate} />
                      )}
                    </td>
                    <td>
                      {!!nextSchedule.date && <FormattedDateShort value={nextSchedule.date} />}
                    </td>
                    <td>
                      {!!nextSchedule.amount && <FormattedCurrency value={nextSchedule.amount} />}
                    </td>
                    <td>
                      {nextSchedule.invoiceText && (
                        <FormattedListValue
                          list="invoicetext"
                          id={nextSchedule.invoiceText.id}
                          fallback={nextSchedule.invoiceText.name}
                        />
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        )}
        {advances && advances.length > 0 && (
          <tr>
            <td className="middle">
              <h4 className="rotated-text">
                <FormattedMessage {...messages.Advances} />
              </h4>
            </td>
            <td colSpan={columns - 1}>
              <table className="table subtable">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...messages.Date} />
                    </th>
                    <th>
                      <FormattedMessage {...messages.Number} />
                    </th>

                    <th>
                      <FormattedMessage {...messages.Amount} />
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {advances.map(a => (
                    <tr key={a.id}>
                      <td>{a.date && <FormattedDateShort value={a.date} />}</td>
                      <td>{a.number}</td>
                      <td>{a.amount && <FormattedCurrency value={a.amount} />}</td>
                      <td className="text-right">
                        <CanRead on="InvoiceFinal">
                          {a.documentPdfId && (
                            <span title={intl.formatMessage(messages.OpenPdf)} className="margin5R">
                              <FaFilePdf
                                className="react-icon "
                                onClick={() => DocumentService.open(a.documentPdfId)}
                              />
                            </span>
                          )}
                          <LinkIcon
                            tooltip={intl.formatMessage(messages.OpenInvoiceOverview)}
                            onClick={() => stateService.gotoInvoiceOverview({ invoiceId: a.id })}
                          />
                        </CanRead>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        )}
      </Fragment>
    );
  }
}
ToInvoiceDetail.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};
export default injectIntl(ToInvoiceDetail);
